import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { MessageCircle, X, User, ArrowUpRight, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Removed Azure imports
import Groq from "groq-sdk";

// API key is injected from the environment (VITE_GLLM). It is configured
// in Vercel → Project Settings → Environment Variables and never committed.
// For local dev, run `vercel env pull` (or copy .env.example to .env.local).
const groq = new Groq({
  apiKey: import.meta.env.VITE_GLLM,
  dangerouslyAllowBrowser: true
});

import ChatMessages from './ChatMessages';

const DolphinIcon = () => (
  <svg 
    viewBox="0 0 24 24" 
    className="w-4 h-4"
    fill="currentColor"
  >
    <path d="M21.32,3.27c-.55-.35-1.13-.52-1.71-.52s-1.16,.17-1.71,.52l-1.4,.89v-1.16c0-1.1-.9-2-2-2H6.5c-1.1,0-2,.9-2,2v3.78c-1.76,.89-3,2.71-3,4.82v4.8c0,2.76,2.24,5,5,5h2c1.1,0,2-.9,2-2v-1h2v1c0,1.1,.9,2,2,2h2c2.76,0,5-2.24,5-5v-4.8c0-1.23-.45-2.35-1.18-3.22l1.18-.75v1.97c0,.55,.45,1,1,1s1-.45,1-1v-4c0-.58-.17-1.16-.52-1.71-.35-.55-.83-.98-1.38-1.27zm-2.82,12.33c0,1.65-1.35,3-3,3h-2v-1c0-1.1-.9-2-2-2h-2c-1.1,0-2,.9-2,2v1h-2c-1.65,0-3-1.35-3-3v-4.8c0-1.65,1.35-3,3-3h8c1.65,0,3,1.35,3,3v4.8z"/>
    <circle cx="8.5" cy="11.5" r="1.5"/>
  </svg>
);
const SYSTEM_PROMPT = `You are Dolphy, DolphAI's friendly chatbot assistant. Keep responses very short and conversational.

Company Overview:
DolphAI is a tech startup specializing in AI solutions, process automation, and platform development.

Quick Navigation:
- Home (/): Overview and services
- Services (/services): AI, Automation, Platforms
- Case Studies (/case-studies): Success stories
- Blog (/blog): Tech insights
- About (/about): Company info
- Contact (/contact): Get in touch
- Careers (/careers): Jobs
- Press (/press): News

Notable Projects:
- Millennium Semiconductors: AI workflow automation
- Agam Enterprises: Website and AI Automation in workflow
- Coolvaccs Agencies: Website and complete ai powered enviornment to process data and autmate process and save 60+ hours each week
- Whale Legal: Legal document automation (Demo: https://whale-legal.vercel.app/)
- Whale Finance: Bank statement processing (Demo: https://whale-finance-alpha.vercel.app/)

Contact:
- Email: peek.nahar@gmail.com
- Social: GitHub, Twitter, LinkedIn (@dolph_ai)

Guidelines:
- Keep responses under 2-3 sentences when possible
- Be friendly and conversational
- Share relevant links when needed
- Direct to contact form for specific inquiries
- Mention demos when relevant
- Focus on solutions over technical details
- Include call-to-actions when appropriate

Remember to be concise and helpful while maintaining a friendly tone.`;



const formatInternalLinks = (text) => {
  // Convert plain text paths (e.g., /services, /about) to markdown links.
  // This regex matches any substring that starts with "/" preceded by beginning or whitespace.
  return text.replace(/(^|\s)(\/[\w/-]+)/g, (match, pre, path) => {
    // Remove any trailing punctuation (if needed)
    const cleanPath = path.replace(/[.,?!]$/, '');
    const trailing = path.slice(cleanPath.length);
    const display = cleanPath.substring(1);
    return `${pre}[${display}](${cleanPath})${trailing}`;
  });
};

const formatMessageContent = (text) => {
  if (!text) return null;
  const formattedText = formatInternalLinks(text);
  const isExternalLink = (href) => {
    try { return href.startsWith('http') || href.startsWith('https'); }
    catch { return false; }
  };
  
  return (
    <ReactMarkdown
      components={{
        a: ({ node, children, href, ...rest }) => {
          const linkClasses = "text-blue-500 hover:text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1";
          if (isExternalLink(href)) {
            return (
              <a href={href} {...rest} className={linkClasses} target="_blank" rel="noopener noreferrer">
                {children}
                <ArrowUpRight className="w-3 h-3" />
              </a>
            );
          }
          return (
            <Link to={href} {...rest} className={linkClasses}>
              {children}
            </Link>
          );
        },
        p: ({ node, ...props }) => <p {...props} className="mb-2" />
        // ...other components...
      }}
    >
      {formattedText}
    </ReactMarkdown>
  );
};

const ChatMessage = ({ message }) => {
  if (!message) return null;

  return (
    <div className={`flex items-start gap-2 ${message.isBot ? 'justify-start' : 'justify-end'}`}>
      {message.isBot && (
        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
          <DolphinIcon />
        </div>
      )}
      <div
        className={`group relative max-w-xs md:max-w-sm p-3 rounded-lg ${
          message.isBot
            ? 'bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700'
            : 'bg-blue-600 text-white'
        }`}
      >
        <div className={`text-sm ${message.isBot ? 'text-gray-800 dark:text-gray-200' : 'text-white'}`}>
          {formatMessageContent(message.text)}
        </div>
      </div>
      {!message.isBot && (
        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
};

const ErrorDisplay = ({ message }) => (
  <div className="p-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded">
    {message}
  </div>
);

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Hello! How can I help you navigate our website and services today?", isBot: true }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeout = useRef(null);

  const handleOpenChat = () => {
    setIsAnimating(true);
    setIsOpen(true);

    animationTimeout.current = setTimeout(() => {
      setIsAnimating(false);
    }, 1000);
  };

  const handleCloseChat = () => {
    setIsOpen(false);
    clearTimeout(animationTimeout.current);
    setIsAnimating(false);
  };

  // Removed useEffect for Azure client initialization

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { text: userMessage, isBot: false }]);
    setLoading(true);
    setError(null);

    try {
      // Use Groq instead of Azure LLM
      const stream = await groq.chat.completions.create({
        messages: [
          { role: "system", content: SYSTEM_PROMPT },
          ...messages.map(msg => ({
            role: msg.isBot ? "assistant" : "user",
            content: msg.text
          })),
          { role: "user", content: userMessage }
        ],
        model: "llama-3.3-70b-versatile",
        temperature: 0.5,
        max_completion_tokens: 1024,
        top_p: 1,
        stop: ", 6",
        stream: true,
      });

      let fullResponse = "";
      for await (const chunk of stream) {
        fullResponse += chunk.choices[0]?.delta?.content || "";
        // Optionally update UI for partial responses here
      }
      setMessages(prev => [...prev, { text: fullResponse, isBot: true }]);
    } catch (err) {
      console.error("Error getting response:", err);
      setMessages(prev => [...prev, {
        text: "I apologize, but I'm having trouble responding right now. Please try again.",
        isBot: true
      }]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen ? (
        <motion.button
          onClick={handleOpenChat}
          className="relative bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors overflow-hidden"
          aria-label="Open chat"
          style={{ width: '56px', height: '56px' }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
        >
          <AnimatePresence>
            {isAnimating && (
              <motion.div
                key="animation"
                className="absolute inset-0 bg-blue-500 rounded-full transform origin-center"
                style={{ clipPath: 'circle(30%)' }}
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 2, opacity: 0 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 1, ease: "easeInOut" }}
              />
            )}
          </AnimatePresence>
          <MessageCircle size={24} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </motion.button>
      ) : (
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-80 md:w-96 border border-gray-200 dark:border-gray-700 flex flex-col h-[500px]"
          initial={{ opacity: 0, y: 50, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">Dolphy</h3>
            </div>
            <button
              onClick={handleCloseChat}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="flex-grow overflow-y-auto">
            <ChatMessages 
              messages={messages}
              loading={loading}
              error={error}
            />
          </div>

          <form onSubmit={handleSubmit} className="border-t dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
            <div className="relative flex items-center">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                disabled={loading}
                aria-label="Chat input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="absolute right-2 p-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <Send size={20} />
              </button>
            </div>
          </form>
        </motion.div>
      )}
    </div>
  );
};

export default Chatbot;
